(() => {
  const qsa = (root, selector) => [...root.querySelectorAll(selector)];
  const formatMoney = (cents, format) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, format);
    const amount = (Number(cents || 0) / 100).toFixed(2);
    return String(format || '{{amount}}').replace(/\{\{\s*amount[^}]*\}\}/, amount);
  };

  const cartRoot = () => window.Shopify?.routes?.root || '/';
  const cartUrl = (path) => `${cartRoot().replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  let cartQueue = Promise.resolve();

  const parseSection = (html) => {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html');
  };

  const renderCartSections = (sections, trigger, openDrawer = true) => {
    if (!sections) return;

    const drawerHtml = sections['cart-drawer'];
    const drawerWrapper = document.getElementById('shopify-section-cart-drawer');
    if (drawerHtml && drawerWrapper) {
      const drawerDocument = parseSection(drawerHtml);
      const drawerSource = drawerDocument?.getElementById('shopify-section-cart-drawer');
      drawerWrapper.innerHTML = drawerSource ? drawerSource.innerHTML : drawerHtml;
    }

    const headerDocument = parseSection(sections['rosemary-header']);
    const renderedCount = headerDocument?.querySelector('[data-cart-count]')?.textContent?.trim();
    if (renderedCount != null && renderedCount !== '') {
      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = renderedCount;
      });
      window.RosemaryCart = window.RosemaryCart || {};
      window.RosemaryCart.lastCountSync = Date.now();
    }

    if (openDrawer) {
      const drawer = document.querySelector('[data-cart-drawer]');
      if (drawer && typeof drawer.open === 'function') drawer.open(trigger || document.activeElement);
    }
  };

  const performCartAdd = async (items, { openDrawer = true, trigger = null } = {}) => {
    const payload = { items };
    const drawerEnabled = openDrawer && window.theme?.settings?.enableCartDrawer !== false;
    if (openDrawer) {
      payload.sections = drawerEnabled ? ['cart-drawer', 'rosemary-header'] : ['rosemary-header'];
      payload.sections_url = `${window.location.pathname}${window.location.search}`;
    }

    const response = await fetch(cartUrl('cart/add.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    });
    const result = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(result.description || result.message || 'تعذر إضافة المنتج للسلة');
    }

    if (openDrawer) renderCartSections(result.sections, trigger, drawerEnabled);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: result } }));
    return result;
  };

  const addCartItems = (items, options = {}) => {
    const task = () => performCartAdd(items, options);
    const request = cartQueue.then(task, task);
    cartQueue = request.catch(() => undefined);
    return request;
  };

  window.RosemaryCart = Object.assign(window.RosemaryCart || {}, { addItems: addCartItems });

  const itemFromProductForm = (form) => {
    const data = new FormData(form);
    const item = {
      id: Number(data.get('id')),
      quantity: Math.max(1, Number.parseInt(data.get('quantity') || '1', 10) || 1)
    };
    const properties = {};

    data.forEach((value, key) => {
      const property = key.match(/^properties\[(.+)\]$/);
      if (property && value !== '') properties[property[1]] = value;
    });

    if (Object.keys(properties).length) item.properties = properties;
    if (data.get('selling_plan')) item.selling_plan = Number(data.get('selling_plan'));
    return item;
  };

  const bindAjaxProductForm = (form, submit) => {
    if (!form || !submit || form.dataset.rpCartReady === 'true') return;
    form.dataset.rpCartReady = 'true';
    const originalLabel = submit.textContent;

    form.addEventListener('submit', async (event) => {
      if (event.submitter && event.submitter !== submit) return;
      event.preventDefault();
      event.stopPropagation();
      if (submit.disabled) return;

      const item = itemFromProductForm(form);
      if (!item.id) return;

      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      submit.textContent = 'جاري الإضافة…';

      try {
        await addCartItems([item], { openDrawer: true, trigger: submit });
        submit.textContent = 'تمت الإضافة ✓';
      } catch (error) {
        submit.textContent = error.message || 'حاولي مرة أخرى';
      } finally {
        window.setTimeout(() => {
          submit.disabled = false;
          submit.removeAttribute('aria-busy');
          submit.textContent = originalLabel;
        }, 1200);
      }
    });
  };

  const updateStockState = (form, variant) => {
    const alert = form.querySelector('[data-rp-stock-alert]');
    const quantityInput = form.querySelector('[data-rp-quantity-input]');
    const quantity = Number(variant?.inventory_quantity || 0);
    const tracked = Boolean(variant?.inventory_management);
    const available = Boolean(variant?.available);
    const threshold = Number(alert?.dataset.threshold || 10);
    const showAlert = Boolean(alert) && tracked && available && quantity > 0 && quantity <= threshold;

    if (alert) {
      alert.hidden = !showAlert;
      if (showAlert) {
        const label = alert.dataset.label || '';
        const suffix = alert.dataset.suffix || '';
        const text = alert.querySelector('[data-rp-stock-text]');
        const progress = alert.querySelector('[data-rp-stock-progress]');
        if (text) text.textContent = [label, '—', quantity, suffix].filter(Boolean).join(' ');
        if (progress) progress.style.setProperty('--rp-stock-progress', `${Math.min(100, (quantity / threshold) * 100)}%`);
      }
    }

    if (quantityInput) {
      const shouldLimit = tracked && variant?.inventory_policy !== 'continue' && quantity > 0;
      if (shouldLimit) quantityInput.max = String(quantity);
      else quantityInput.removeAttribute('max');
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const initGallery = (root) => {
    const gallery = root.querySelector('[data-rp-gallery]');
    if (!gallery) return;

    const slides = qsa(gallery, '[data-rp-stage-media]');
    const thumbs = qsa(gallery, '[data-rp-thumb]');
    const thumbsRail = gallery.querySelector('[data-rp-thumbs]');
    const stage = gallery.querySelector('[data-rp-stage]');
    const counter = gallery.querySelector('[data-rp-counter]');
    const dialog = root.querySelector('[data-rp-lightbox]');
    const dialogMedia = dialog?.querySelector('[data-rp-lightbox-media]');
    const dialogCounter = dialog?.querySelector('[data-rp-lightbox-counter]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let returnFocus = null;

    const normaliseIndex = (index) => (index + slides.length) % slides.length;

    const pauseMedia = (container) => {
      if (!container) return;
      container.querySelectorAll('video').forEach((video) => video.pause());
      container.querySelectorAll('model-viewer').forEach((model) => model.pause?.());
      container.querySelectorAll('iframe').forEach((frame) => {
        if (frame.src) frame.src = frame.src;
      });
    };

    const keepThumbVisible = (thumb) => {
      if (!thumbsRail || !thumb) return;
      const top = thumb.offsetTop;
      const bottom = top + thumb.offsetHeight;
      if (top < thumbsRail.scrollTop) thumbsRail.scrollTop = top;
      else if (bottom > thumbsRail.scrollTop + thumbsRail.clientHeight) thumbsRail.scrollTop = bottom - thumbsRail.clientHeight;
    };

    const renderDialogMedia = () => {
      if (!dialogMedia) return;
      pauseMedia(dialogMedia);
      dialogMedia.replaceChildren();
      const source = slides[activeIndex]?.querySelector('img, video, iframe, model-viewer');
      if (!source) return;
      const clone = source.cloneNode(true);
      clone.removeAttribute('id');
      if (clone.tagName === 'IMG') {
        clone.loading = 'eager';
        clone.sizes = '90vw';
      }
      if (clone.tagName === 'VIDEO') clone.controls = true;
      dialogMedia.append(clone);
      if (dialogCounter) dialogCounter.textContent = `${activeIndex + 1} / ${slides.length}`;
    };

    const selectMedia = (index, { updateDialog = true } = {}) => {
      if (!slides.length) return;
      const nextIndex = normaliseIndex(index);
      const previousSlide = slides[activeIndex];
      const nextSlide = slides[nextIndex];
      if (previousSlide && previousSlide !== nextSlide) pauseMedia(previousSlide);

      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === nextIndex;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });

      if (nextIndex !== activeIndex && !reduceMotion.matches) {
        nextSlide.classList.remove('is-entering');
        void nextSlide.offsetWidth;
        nextSlide.classList.add('is-entering');
      }

      activeIndex = nextIndex;
      thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === activeIndex;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', String(active));
      });
      keepThumbVisible(thumbs[activeIndex]);
      if (counter) counter.textContent = `${activeIndex + 1} / ${slides.length}`;
      if (updateDialog && dialog?.open) renderDialogMedia();
    };

    const move = (step) => selectMedia(activeIndex + step);

    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => selectMedia(index)));
    gallery.querySelector('[data-rp-prev]')?.addEventListener('click', () => move(-1));
    gallery.querySelector('[data-rp-next]')?.addEventListener('click', () => move(1));

    const cleanupDialog = () => {
      pauseMedia(dialogMedia);
      dialogMedia?.replaceChildren();
      document.body.classList.remove('rp-lightbox-open');
      const focusTarget = slides[activeIndex]?.querySelector('[data-rp-open-modal]') || returnFocus;
      focusTarget?.focus({ preventScroll: true });
      returnFocus = null;
    };

    const closeDialog = () => {
      if (!dialog?.open) return;
      dialog.close();
      cleanupDialog();
    };

    const openDialog = (opener) => {
      if (!dialog || !dialogMedia) return;
      returnFocus = opener;
      renderDialogMedia();
      document.body.classList.add('rp-lightbox-open');
      dialog.showModal();
      dialog.querySelector('[data-rp-lightbox-close]')?.focus({ preventScroll: true });
    };

    qsa(gallery, '[data-rp-open-modal]').forEach((button) => button.addEventListener('click', () => openDialog(button)));
    dialog?.querySelector('[data-rp-lightbox-close]')?.addEventListener('click', closeDialog);
    dialog?.querySelector('[data-rp-lightbox-prev]')?.addEventListener('click', () => move(-1));
    dialog?.querySelector('[data-rp-lightbox-next]')?.addEventListener('click', () => move(1));

    dialog?.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });
    dialog?.addEventListener('close', cleanupDialog);
    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog || event.target === dialogMedia) closeDialog();
    });
    dialog?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        move(document.documentElement.dir === 'rtl' ? 1 : -1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        move(document.documentElement.dir === 'rtl' ? -1 : 1);
      }
    });

    const addSwipe = (element) => {
      if (!element || slides.length < 2) return;
      let startX = 0;
      let startY = 0;
      element.addEventListener('touchstart', (event) => {
        startX = event.changedTouches[0].clientX;
        startY = event.changedTouches[0].clientY;
      }, { passive: true });
      element.addEventListener('touchend', (event) => {
        const deltaX = event.changedTouches[0].clientX - startX;
        const deltaY = event.changedTouches[0].clientY - startY;
        if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        const rtl = document.documentElement.dir === 'rtl';
        move(deltaX < 0 ? (rtl ? -1 : 1) : (rtl ? 1 : -1));
      }, { passive: true });
    };

    addSwipe(stage);
    addSwipe(dialogMedia);
    selectMedia(activeIndex, { updateDialog: false });
  };

  const initNormalForm = (root) => {
    const form = root.querySelector('[data-rp-product-form]');
    if (!form) return;

    let variants = [];
    try { variants = JSON.parse(form.querySelector('[data-rp-variants]')?.textContent || '[]'); } catch (_) {}

    const idInput = form.querySelector('[data-rp-variant-id]');
    const submit = form.querySelector('.rp__atc');
    const price = root.querySelector('[data-rp-price-wrap] [data-price]');
    const sku = root.querySelector('[data-rp-sku-value]');
    const addLabel = form.dataset.addLabel || 'Add to cart';
    const soldOutLabel = form.dataset.soldOutLabel || 'Sold out';

    const applyVariant = (variant) => {
      if (!variant) {
        submit.disabled = true;
        submit.textContent = soldOutLabel;
        if (sku) sku.textContent = '';
        updateStockState(form, null);
        return;
      }

      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
      updateStockState(form, variant);
      if (sku) sku.textContent = variant.sku || '';
      submit.disabled = !variant.available;
      submit.textContent = variant.available ? addLabel : soldOutLabel;

      if (price) {
        const onSale = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
        price.classList.toggle('price--on-sale', onSale);
        const regular = price.querySelector('.price__regular');
        const sale = price.querySelector('.price__sale');
        const compare = price.querySelector('.price__compare');
        if (regular) regular.textContent = formatMoney(variant.price, form.dataset.moneyFormat);
        if (sale) sale.textContent = formatMoney(variant.price, form.dataset.moneyFormat);
        if (compare) compare.textContent = onSale ? formatMoney(variant.compare_at_price, form.dataset.moneyFormat) : '';
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      if (variant.featured_media?.id) {
        root.querySelector(`[data-rp-thumb][data-target-media="${variant.featured_media.id}"]`)?.click();
      }
    };

    qsa(form, '[data-rp-option]').forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled || button.dataset.available === 'false') return;
        qsa(form, `[data-position="${button.dataset.position}"]`).forEach((item) => {
          const selected = item === button;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-pressed', String(selected));
        });
        const positions = [...new Set(qsa(form, '[data-rp-option]').map((item) => item.dataset.position))];
        const selected = positions.map((position) => form.querySelector(`[data-position="${position}"].is-selected`)?.dataset.value || '');
        const variant = variants.find((item) => item.options.every((value, index) => value === selected[index]));
        applyVariant(variant);
      });
    });

    const initialVariant = variants.find((item) => String(item.id) === String(idInput?.value));
    if (initialVariant) applyVariant(initialVariant);
    bindAjaxProductForm(form, submit);
  };

  const initBundle = (root) => {
    const bundle = root.querySelector('[data-rp-bundle]');
    if (!bundle || bundle.dataset.ready === 'true') return;
    bundle.dataset.ready = 'true';

    const offers = qsa(bundle, '[data-rp-offer]');
    const slots = bundle.querySelector('[data-rp-slots]');
    const template = bundle.querySelector('[data-rp-choice-template]');
    const submit = bundle.querySelector('[data-rp-bundle-submit]');
    const buyNow = bundle.querySelector('[data-rp-bundle-buy]');
    const status = bundle.querySelector('[data-rp-status]');
    const optionLabel = bundle.dataset.optionLabel || 'اختيار';
    const errorOffer = bundle.dataset.errorOffer || 'Choose an offer first.';
    const errorChoices = bundle.dataset.errorChoices || 'Complete every choice.';
    const errorUnavailable = bundle.dataset.errorUnavailable || 'This offer is unavailable.';
    const addedMessage = bundle.dataset.added || 'Added to your cart.';
    const cartError = bundle.dataset.errorCart || 'We could not add this bundle.';

    let activeOffer = null;
    let activeOfferIsFixed = false;
    let requiredQuantity = 0;
    let allocations = [];

    const setStatus = (message, success = false) => {
      status.textContent = message || '';
      status.classList.toggle('is-success', success);
    };

    const totalSelected = () => allocations.reduce((sum, item) => sum + item.count, 0);

    const refreshAllocator = () => {
      const total = totalSelected();
      const remaining = Math.max(0, requiredQuantity - total);
      const complete = Boolean(activeOffer) && (activeOfferIsFixed || (requiredQuantity > 0 && total === requiredQuantity));
      const singleChoice = requiredQuantity === 1;

      allocations.forEach((item) => {
        item.value.textContent = String(item.count);
        item.row.classList.toggle('has-count', item.count > 0);
        item.row.classList.toggle('is-selected', singleChoice && item.count > 0);
        if (singleChoice) item.row.setAttribute('aria-checked', String(item.count > 0));
        item.minus.disabled = item.count <= 0 || !item.available;
        item.plus.disabled = !item.available || total >= requiredQuantity;
      });

      const summary = slots.querySelector('[data-rp-allocation-summary]');
      if (summary) {
        const selectedColour = singleChoice ? allocations.find((item) => item.count > 0) : null;
        summary.textContent = singleChoice
          ? (selectedColour ? `تم اختيار ${optionLabel}: ${selectedColour.title}` : `اختاري ${optionLabel}`)
          : (complete
            ? `اكتمل العدد: ${total} من ${requiredQuantity}`
            : `تم اختيار ${total} من ${requiredQuantity} — متبقي ${remaining}`);
        summary.classList.toggle('is-complete', complete);
      }

      submit.disabled = !complete;
      if (buyNow) buyNow.disabled = !complete;
    };

    const buildAllocator = (quantity, fixedBundle = false) => {
      requiredQuantity = quantity;
      allocations = [];
      slots.replaceChildren();
      slots.hidden = fixedBundle;

      if (fixedBundle) {
        refreshAllocator();
        return;
      }

      const fieldset = document.createElement('fieldset');

      const legend = document.createElement('legend');

      const grid = document.createElement('div');
      grid.append(template.content.cloneNode(true));

      const rows = qsa(grid, '[data-rp-choice]');
      const availableRows = rows.filter((row) => row.dataset.available === 'true');
      const singleSource = rows.length === 1 && availableRows.length === 1;
      const singleChoice = quantity === 1 && !singleSource;
      fieldset.className = singleChoice
        ? 'rp-bundle__slot rp-bundle__allocator rp-bundle__selector'
        : 'rp-bundle__slot rp-bundle__allocator';
      legend.textContent = singleChoice ? `اختاري ${optionLabel}` : `وزعي عدد القطع على ${optionLabel}`;
      grid.className = singleChoice
        ? 'rp-bundle__choices rp-bundle__choices--single'
        : 'rp-bundle__choices rp-bundle__choices--counter';
      const baseCount = !singleChoice && availableRows.length ? Math.floor(quantity / availableRows.length) : 0;
      let remainder = !singleChoice && availableRows.length ? quantity % availableRows.length : 0;

      rows.forEach((row) => {
        const available = row.dataset.available === 'true';
        const minus = row.querySelector('[data-rp-count-minus]');
        const plus = row.querySelector('[data-rp-count-plus]');
        const value = row.querySelector('[data-rp-count-value]');
        if (!minus || !plus || !value) return;

        const item = {
          row,
          minus,
          plus,
          value,
          available,
          productId: row.dataset.productId,
          variantId: row.dataset.variantId,
          title: row.dataset.title,
          count: available ? (singleSource ? quantity : (!singleChoice ? baseCount + (remainder > 0 ? 1 : 0) : 0)) : 0
        };
        if (available && !singleChoice && remainder > 0) remainder -= 1;
        allocations.push(item);

        if (singleSource) {
          row.setAttribute('aria-hidden', 'true');
        } else if (singleChoice) {
          row.setAttribute('role', 'radio');
          row.setAttribute('aria-checked', 'false');
          row.tabIndex = available && row === availableRows[0] ? 0 : -1;

          const chooseColour = () => {
            if (!item.available) return;
            allocations.forEach((candidate) => {
              candidate.count = 0;
              candidate.row.tabIndex = -1;
            });
            item.count = 1;
            item.row.tabIndex = 0;
            setStatus('');
            refreshAllocator();
          };

          row.addEventListener('click', chooseColour);
          row.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            chooseColour();
          });
        } else {
          minus.addEventListener('click', () => {
            if (!item.available || item.count <= 0) return;
            item.count -= 1;
            setStatus('');
            refreshAllocator();
          });

          plus.addEventListener('click', () => {
            if (!item.available || totalSelected() >= requiredQuantity) return;
            item.count += 1;
            setStatus('');
            refreshAllocator();
          });
        }
      });

      const summary = document.createElement('p');
      summary.className = 'rp-bundle__allocation-summary';
      summary.dataset.rpAllocationSummary = '';
      summary.setAttribute('aria-live', 'polite');

      fieldset.append(legend, grid, summary);
      fieldset.hidden = singleSource;
      slots.append(fieldset);
      refreshAllocator();
    };

    offers.forEach((offer) => {
      offer.addEventListener('click', () => {
        if (offer.dataset.available !== 'true' || offer.disabled || !offer.dataset.variantId) return;
        activeOffer = offer;
        activeOfferIsFixed = offer.dataset.rpFixedBundle === 'true';
        offers.forEach((item) => {
          const selected = item === offer;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-checked', String(selected));
        });
        setStatus('');
        buildAllocator(Number(offer.dataset.quantity || 1), activeOfferIsFixed);
      });
    });

    const defaultOffer = offers.find((offer) => offer.dataset.rpDefault === 'true' && !offer.disabled)
      || offers.find((offer) => !offer.disabled);
    defaultOffer?.click();

    const addBundle = async (goToCheckout = false) => {
      if (!activeOffer) return setStatus(errorOffer);
      if (!activeOfferIsFixed && totalSelected() !== requiredQuantity) {
        setStatus(errorChoices);
        refreshAllocator();
        return;
      }

      const selectedSingle = allocations.find((item) => item.count > 0);
      const selectedSingleVariant = activeOffer.dataset.rpSingle === 'true' ? selectedSingle?.variantId : null;
      let randomVariantIds = [];
      try {
        randomVariantIds = JSON.parse(activeOffer.dataset.rpRandomVariantIds || '[]');
      } catch (error) {
        randomVariantIds = [];
      }
      const randomVariantId = randomVariantIds.length
        ? randomVariantIds[Math.floor(Math.random() * randomVariantIds.length)]
        : null;
      const variantId = selectedSingleVariant || randomVariantId || activeOffer.dataset.variantId;
      if (!variantId) return setStatus(errorUnavailable);

      const properties = {
        '_Rosemary Bundle': 'true',
        '_Bundle Quantity': activeOfferIsFixed ? '1' : String(requiredQuantity)
      };

      allocations.filter((item) => item.count > 0).forEach((item) => {
        properties[`${optionLabel} - ${item.title}`] = `${item.count} قطعة`;
      });

      submit.disabled = true;
      if (buyNow) buyNow.disabled = true;
      setStatus('');

      try {
        await addCartItems([{ id: Number(variantId), quantity: 1, properties }], {
          openDrawer: !goToCheckout,
          trigger: submit
        });

        setStatus(addedMessage, true);
        if (goToCheckout) window.location.assign(cartUrl('checkout'));
      } catch (error) {
        setStatus(error.message || cartError);
      } finally {
        refreshAllocator();
      }
    };

    submit.addEventListener('click', () => addBundle(false));
    buyNow?.addEventListener('click', () => addBundle(true));
  };

  const initFallbackForm = (root) => {
    const form = root.querySelector('[data-rp-fallback-form]');
    if (!form) return;

    const idInput = form.querySelector('[data-rp-fallback-variant]');
    const submit = form.querySelector('.rp__atc');
    const price = root.querySelector('[data-rp-price-wrap] [data-price]');
    const addLabel = form.dataset.addLabel || 'Add to cart';
    const soldOutLabel = form.dataset.soldOutLabel || 'Sold out';

    qsa(form, '[data-rp-fallback-choice]').forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        qsa(form, '[data-rp-fallback-choice]').forEach((item) => {
          const selected = item === button;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-pressed', String(selected));
        });

        const available = button.dataset.available === 'true';
        idInput.value = button.dataset.variantId;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
        updateStockState(form, {
          available,
          inventory_quantity: Number(button.dataset.inventory || 0),
          inventory_management: button.dataset.inventoryManagement || null,
          inventory_policy: button.dataset.inventoryPolicy || 'deny'
        });
        submit.disabled = !available;
        submit.textContent = available ? addLabel : soldOutLabel;

        if (price) {
          const variantPrice = Number(button.dataset.price || 0);
          const compareAtPrice = Number(button.dataset.compareAtPrice || 0);
          const onSale = compareAtPrice > variantPrice;
          price.classList.toggle('price--on-sale', onSale);
          const regular = price.querySelector('.price__regular');
          const sale = price.querySelector('.price__sale');
          const compare = price.querySelector('.price__compare');
          if (regular) regular.textContent = formatMoney(variantPrice, form.dataset.moneyFormat);
          if (sale) sale.textContent = formatMoney(variantPrice, form.dataset.moneyFormat);
          if (compare) compare.textContent = onSale ? formatMoney(compareAtPrice, form.dataset.moneyFormat) : '';
        }
      });
    });
    bindAjaxProductForm(form, submit);
  };

  const initQuantitySelectors = (root) => {
    qsa(root, '[data-rp-quantity]').forEach((selector) => {
      if (selector.dataset.ready === 'true') return;

      const input = selector.querySelector('[data-rp-quantity-input]');
      const minus = selector.querySelector('[data-rp-quantity-minus]');
      const plus = selector.querySelector('[data-rp-quantity-plus]');
      if (!input || !minus || !plus) return;

      selector.dataset.ready = 'true';

      const update = (value = input.value) => {
        const min = Number(input.min || 1);
        const max = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
        const parsed = Number.parseInt(value, 10);
        const next = Math.min(max, Math.max(min, Number.isFinite(parsed) ? parsed : min));

        input.value = String(next);
        minus.disabled = next <= min;
        plus.disabled = Number.isFinite(max) && next >= max;
      };

      minus.addEventListener('click', () => update(Number(input.value) - 1));
      plus.addEventListener('click', () => update(Number(input.value) + 1));
      input.addEventListener('change', () => update());
      input.addEventListener('blur', () => update());

      update();
    });
  };

  const initCrossSell = (root) => {
    qsa(root, '[data-rp-cross-sell-form]').forEach((form) => {
      if (form.dataset.ready === 'true') return;

      const select = form.querySelector('[data-rp-cross-sell-select]');
      const idInput = form.querySelector('[data-rp-cross-sell-id]');
      const submit = form.querySelector('[data-rp-cross-sell-submit]');
      const status = form.querySelector('[data-rp-cross-sell-status]');
      const item = form.closest('.rp-cross-sell__item');
      const price = item?.querySelector('[data-rp-cross-sell-price]');
      if (!submit) return;

      form.dataset.ready = 'true';
      const label = submit.dataset.label || submit.textContent.trim() || '+ أضف';

      select?.addEventListener('change', () => {
        const selectedOption = select.selectedOptions[0];
        if (price && selectedOption?.dataset.price) price.textContent = selectedOption.dataset.price;
      });

      submit.addEventListener('click', async () => {
        const variantId = Number(select?.value || idInput?.value);
        if (!variantId) return;

        submit.disabled = true;
        submit.classList.remove('is-added');
        submit.textContent = 'جاري الإضافة…';
        if (status) status.textContent = '';

        try {
          await addCartItems([{ id: variantId, quantity: 1 }], { openDrawer: true, trigger: submit });

          submit.classList.add('is-added');
          submit.textContent = 'تمت الإضافة ✓';
          if (status) status.textContent = 'تمت إضافة المنتج للسلة';
        } catch (error) {
          submit.textContent = 'حاولي مرة أخرى';
          if (status) status.textContent = error.message || 'تعذر إضافة المنتج';
        } finally {
          window.setTimeout(() => {
            submit.disabled = false;
            submit.classList.remove('is-added');
            submit.textContent = label;
          }, 1400);
        }
      });
    });
  };

  const initCrossSellPlacement = (root) => {
    const crossSell = root.querySelector('[data-rp-cross-sell]');
    const home = root.querySelector('[data-rp-cross-sell-home]');
    const mobileTarget = root.querySelector('[data-rp-cross-sell-mobile-target]');
    if (!crossSell || !home || !mobileTarget) return;

    const mobileView = window.matchMedia('(max-width: 749px)');
    const updatePlacement = () => {
      if (mobileView.matches) {
        if (crossSell.parentElement !== mobileTarget) mobileTarget.append(crossSell);
      } else if (home.parentElement && crossSell.previousElementSibling !== home) {
        home.after(crossSell);
      }
    };

    updatePlacement();
    if (typeof mobileView.addEventListener === 'function') mobileView.addEventListener('change', updatePlacement);
    else mobileView.addListener(updatePlacement);
  };

  const initCrossSellLoader = async (root) => {
    const loader = root.querySelector('[data-rp-cross-sell-loader]');
    if (!loader || loader.dataset.ready === 'true' || !loader.dataset.url) return;
    loader.dataset.ready = 'true';

    try {
      const response = await fetch(loader.dataset.url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Recommendations request failed');
      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const crossSell = documentFragment.querySelector('[data-rp-cross-sell]');
      if (!crossSell) {
        loader.remove();
        return;
      }

      loader.replaceWith(crossSell);
      initCrossSellPlacement(root);
      initCrossSell(root);
    } catch (error) {
      loader.remove();
    }
  };

  const initRecommendationsSliders = (scope = document) => {
    const sections = [];
    if (scope.matches?.('.rp__recommendations')) sections.push(scope);
    scope.querySelectorAll?.('.rp__recommendations').forEach((section) => sections.push(section));

    sections.forEach((section) => {
      if (section.dataset.rpSliderWatching === 'true') return;
      section.dataset.rpSliderWatching = 'true';

      const setup = () => {
        const track = section.querySelector('[data-rp-recommendations-track]');
        if (!track || !track.children.length || track.dataset.rpSliderReady === 'true') return;
        track.dataset.rpSliderReady = 'true';

        const cards = [...track.children];
        const previousButton = section.querySelector('[data-rp-recommendations-prev]');
        const nextButton = section.querySelector('[data-rp-recommendations-next]');
        const controls = section.querySelector('[data-rp-recommendations-controls]');
        const dots = section.querySelector('[data-rp-recommendations-dots]');
        const isRtl = document.documentElement.dir === 'rtl';
        let activeIndex = 0;
        let scrollFrame = 0;

        section.querySelectorAll('[data-rp-recommendation-add], [data-rp-recommendation-buy]').forEach((button) => {
          if (button.dataset.rpActionReady === 'true') return;
          button.dataset.rpActionReady = 'true';

          button.addEventListener('click', async () => {
            const variantId = Number(button.dataset.variantId);
            const actions = button.closest('.rp-recommendations__actions');
            const status = actions?.querySelector('[data-rp-recommendation-status]');
            const siblingButtons = actions?.querySelectorAll('button') || [];
            const goToCheckout = button.matches('[data-rp-recommendation-buy]');
            let navigatingToCheckout = false;
            if (!variantId) return;

            siblingButtons.forEach((item) => { item.disabled = true; });
            if (status) {
              status.classList.remove('is-error');
              status.textContent = 'جاري الإضافة…';
            }

            try {
              await addCartItems([{ id: variantId, quantity: 1 }], {
                openDrawer: !goToCheckout,
                trigger: button
              });

              if (status) status.textContent = 'تمت إضافة المنتج للسلة ✓';
              if (goToCheckout) {
                navigatingToCheckout = true;
                window.location.assign(cartUrl('checkout'));
                return;
              }
            } catch (error) {
              if (status) {
                status.classList.add('is-error');
                status.textContent = error.message || 'تعذر إضافة المنتج';
              }
            } finally {
              if (!navigatingToCheckout) {
                window.setTimeout(() => {
                  siblingButtons.forEach((item) => { item.disabled = false; });
                  if (status) status.textContent = '';
                }, 1500);
              }
            }
          });
        });

        const nearestIndex = () => {
          const trackRect = track.getBoundingClientRect();
          const trackEdge = isRtl ? trackRect.right : trackRect.left;
          let closestIndex = 0;
          let closestDistance = Number.POSITIVE_INFINITY;

          cards.forEach((card, index) => {
            const cardRect = card.getBoundingClientRect();
            const cardEdge = isRtl ? cardRect.right : cardRect.left;
            const distance = Math.abs(cardEdge - trackEdge);
            if (distance < closestDistance) {
              closestDistance = distance;
              closestIndex = index;
            }
          });

          return closestIndex;
        };

        const update = () => {
          activeIndex = nearestIndex();
          if (previousButton) previousButton.disabled = activeIndex <= 0;
          if (nextButton) nextButton.disabled = activeIndex >= cards.length - 1;
          dots?.querySelectorAll('button').forEach((dot, index) => {
            const active = index === activeIndex;
            dot.classList.toggle('is-active', active);
            dot.setAttribute('aria-current', active ? 'true' : 'false');
          });
        };

        const goTo = (index) => {
          activeIndex = Math.max(0, Math.min(cards.length - 1, index));
          cards[activeIndex].scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'start'
          });
          window.setTimeout(update, 260);
        };

        if (cards.length < 2) {
          if (controls) controls.hidden = true;
          if (dots) dots.hidden = true;
          return;
        }

        previousButton?.addEventListener('click', () => goTo(activeIndex - 1));
        nextButton?.addEventListener('click', () => goTo(activeIndex + 1));
        track.addEventListener('scroll', () => {
          window.cancelAnimationFrame(scrollFrame);
          scrollFrame = window.requestAnimationFrame(update);
        }, { passive: true });

        if (dots) {
          dots.replaceChildren();
          cards.forEach((card, index) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.setAttribute('aria-label', `المنتج ${index + 1}`);
            dot.addEventListener('click', () => goTo(index));
            dots.append(dot);
          });
        }

        window.addEventListener('resize', update, { passive: true });
        window.requestAnimationFrame(update);
      };

      setup();
      const observer = new MutationObserver(setup);
      observer.observe(section, { childList: true, subtree: true });
    });
  };

  const init = (root) => {
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';
    initGallery(root);
    initQuantitySelectors(root);
    initNormalForm(root);
    initBundle(root);
    initFallbackForm(root);
    initCrossSellPlacement(root);
    initCrossSell(root);
    initCrossSellLoader(root);
    initRecommendationsSliders(root);
  };

  const boot = (scope = document) => {
    if (scope.matches?.('[data-rp-root]')) init(scope);
    scope.querySelectorAll?.('[data-rp-root]').forEach(init);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
